import React from 'react';

import DataTable from 'components/DataTable/DataTable';
import { dateToString } from 'utils/formatDate';
import { Grid, Typography, Box } from '@mui/material';
import styles from './TemplatesTable.module.css';
import MenuButton from 'components/MenuButton/MenuButton';
import { CollectionsBookmarkRounded, Delete, DescriptionRounded, MoreHorizOutlined } from '@mui/icons-material';
import { ReactComponent as DefaultContractIcon } from 'assets/images/default-contract-icon.svg';
import { Template } from 'dataTypes/template';
import { useAppSelector } from 'app/hooks';
import { selectCurrentUser } from 'features/auth/authSlice';
import { UserRoles } from 'features/auth/authTypes';
import { appAccessRoles } from 'App';
import { useNavigate } from 'react-router-dom';

interface TemplateTableProps {
  data: Template[];
  onDelete: (id: number, name: string) => void;
  onRename: (id: number, name: string) => void;
  onSetDefault: (id: number, type: string) => void;
  onCreateFileFromTemplate: (id: number) => void;
}
const TemplateTable: React.FC<TemplateTableProps> = ({
  data,
  onDelete,
  onRename,
  onSetDefault,
  onCreateFileFromTemplate,
}) => {
  const user = useAppSelector(selectCurrentUser);
  const showCreateFileFromTemplateOption = appAccessRoles.includes(user?.role as UserRoles);
  const navigate = useNavigate();

  function handleOpenTemplate(id: number, type: string) {
    if (type) {
      navigate(`/templates/edit/${type.toLowerCase()}/${id}`);
    } else {
      navigate(`/templates/edit/document/${id}`);
    }
  }

  const rowsData = data.map(({ id, name, updatedAt, type, createdAt, createdByUser, isDefault }) => {
    const getMenuOptions = () => {
      const createFileFromTemplateOption = {
        item: (
          <Box display='flex' gap={1} justifyContent='start' alignItems='center'>
            <CollectionsBookmarkRounded color='primary' />
            Create file from template
          </Box>
        ),
        handler: () => {
          onCreateFileFromTemplate(id);
        },
      };

      const defaultOptions = [
        {
          item: (
            <Box display='flex' gap={1} justifyContent='start' alignItems='center'>
              <DescriptionRounded color='primary' />
              Rename
            </Box>
          ),
          handler: () => {
            onRename(id, name);
          },
        },
        {
          item: (
            <Box display='flex' gap={1} justifyContent='start' alignItems='center'>
              <Delete color='primary' />
              Delete
            </Box>
          ),
          handler: () => {
            onDelete(id, name);
          },
        },
      ];

      const setDefaultOption = {
        item: (
          <Box display='flex' gap={1} justifyContent='start' alignItems='center'>
            <DefaultContractIcon />
            {`Set as default ${type.toLowerCase()}`}
          </Box>
        ),
        handler: () => {
          onSetDefault(id, type);
        },
      };

      const menuOptions = isDefault ? defaultOptions : [setDefaultOption, ...defaultOptions];

      return showCreateFileFromTemplateOption ? [createFileFromTemplateOption, ...menuOptions] : menuOptions;
    };

    return {
      name: <Typography style={{cursor: "pointer"}} onClick={() => handleOpenTemplate(id, type)} color={'#315A8A'}>{name}</Typography>,
      userName: createdByUser?.name,
      created: dateToString(createdAt),
      updated: dateToString(updatedAt),
      more: (
        <MenuButton options={getMenuOptions()} tooltipLabel='More Options'>
          <MoreHorizOutlined />
        </MenuButton>
      ),
    };
  });

  return (
    <Grid container className={styles.container}>
      <Grid item xs={12}>
        <DataTable
          columnData={[
            {
              id: 'name',
              name: 'Template Name',
              align: 'left',
              enableSort: true,
            },
            { id: 'username', name: 'Owner', align: 'left' },
            {
              id: 'createdAt',
              name: 'Created Date',
              align: 'left',
              enableSort: true,
            },
            {
              id: 'updatedAt',
              name: 'Last Change',
              align: 'left',
              enableSort: true,
            },
            {
              id: 'more',
              name: 'More',
              align: 'left',
            },
          ]}
          rows={rowsData}
        />
      </Grid>
    </Grid>
  );
};

export default TemplateTable;
